import React, { useState, useEffect } from "react";
import styles from "./EligibilityPricingContainer.module.css";
import Image from "next/image";
import { createPaymentData } from "@/services/paymentService";
import { useRouter } from "next/router";
import { parseFullName } from "@/utils/utils";
import axios from "axios";
import useGeoLocation from "@/services/useGeoLocation";
import {
  PAYMENT_GATEWAY,
  RMS_PRE_ENROLLMENT_BASE_URL,
  VIN_CRM_API_BASE_URL,
} from "@/Config/apiServices";
import { ArrowLeft, ChevronLeft } from "lucide-react";
import { Check } from "lucide-react";
import { Info } from "lucide-react";
import { trackEventPreEnrolment } from "@/services/mixpanel";

// Define the structure for the pricing details
interface PlanDetails {
  price: string;
  text: string;
  duration: string;
}

// Define the structure for each pricing plan (dynamic keys like month, semester, year)
interface PricingPlan {
  month?: PlanDetails;
  semester?: PlanDetails;
  year?: PlanDetails;
}

// Props for EligibilityPricingContainer
interface PricingContainerProps {
  selectedProgram: any | null;
  onBack: () => void;
  eligibilityFormData: any;
  windowHeight?: any;
}

const EligibilityPricingContainer: React.FC<PricingContainerProps> = ({
  selectedProgram,
  onBack,
  eligibilityFormData,
  windowHeight,
}) => {
  const [showPlans, setShowPlans] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // const [regIdData, setRegIdData] = useState<any>(null);
  // const [regId, setRegId] = useState<string>("");
  const router = useRouter();
  const geoLocationData: any = useGeoLocation();

  const fees = eligibilityFormData?.studyModes.fees || [];

  // Dynamic fee values based on study mode
  const semesterFee =
    fees.find((fee: { feeMode: string }) => fee.feeMode === "SEMESTER")?.fee ||
    "0";
  const monthlyFee =
    fees.find((fee: { feeMode: string }) => fee.feeMode === "MONTHLY")?.fee ||
    "0";
  const annuallyFee =
    fees.find((fee: { feeMode: string }) => fee.feeMode === "ANNUALLY")?.fee ||
    "0";
  const applicationFee =
    fees.find((fee: { feeMode: string }) => fee.feeMode === "APPLICATION")
      ?.fee || "0";

  const totalFee =
    fees.find((fee: { feeMode: string }) => fee.feeMode === "TOTAL")?.fee ||
    "0";

  // Fetching RMAT fee
  const rmatFee = eligibilityFormData.rmatFee;

  // Calculate total application fee (including RMAT fee)
  const applicationTotalFee = parseFloat(applicationFee) + parseFloat(rmatFee);

  // Calculate based on program duration
  const numberOfSemesters = selectedProgram.noOfYear * 2; // 2 semesters per year
  const numberOfMonths = selectedProgram.noOfYear * 12; // 12 months per year

  // Format fee values to avoid unnecessary zeros
  const formatFee = (fee: string) => {
    const formattedFee = parseFloat(fee).toFixed(2); // Format to 2 decimal places
    return formattedFee.replace(/\.00$/, ""); // Remove ".00" if it's a round number
  };

  const pricing = [
    {
      month: {
        price: `R ${formatFee(monthlyFee)} / Month`,
        text: "Monthly Fee",
        duration: `${numberOfMonths} months`,
      },
    },
    {
      semester: {
        price: `R ${formatFee(semesterFee)} / Semester`,
        text: "Semester Fee",
        duration: `${numberOfSemesters} Semesters`,
      },
    },
    {
      year: {
        price: `R ${formatFee(annuallyFee)} / Year`,
        text: "Yearly Fee",
        duration: `${selectedProgram.noOfYear} Years`,
      },
    },
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const togglePlans = () => {
    setShowPlans((prev) => !prev);
    trackEventPreEnrolment("available_payment_plans_clicked", {
      program_selected: selectedProgram.name,
    });
  };

  const closePlans = () => setShowPlans(false);

  // create REGID Api calling

  const userDetails = localStorage.getItem("userPayload");
  let userDetailsParse: any = null;
  if (userDetails) {
    userDetailsParse = JSON.parse(userDetails);
  }

  const userFormData = localStorage.getItem("formData");
  let userFormDataParse: any = null;
  if (userFormData) {
    userFormDataParse = JSON.parse(userFormData);
  }

  const userProgramOfSelect = localStorage.getItem("userSelectedProgram");
  let userSelectedProgram: any = null;
  if (userProgramOfSelect) {
    userSelectedProgram = JSON.parse(userProgramOfSelect);
  }

  const proCode = localStorage.getItem("proCode");
  let proCodeParse: any = null;
  if (proCode) {
    proCodeParse = JSON.parse(proCode);
  }

  const { firstName, lastName, middleName } = parseFullName(
    userFormDataParse?.name
  );
  const handleCreateRegId = async () => {
    const payload = {
      firstName: firstName,
      middleName: middleName,
      lastName: lastName,
      email: userFormDataParse?.email,
      mobileNumber: userDetailsParse?.mobileNumber,
      mobileCountryCode: userDetailsParse?.mobileCountryCode,
      programName: userSelectedProgram,
      programCode: proCodeParse,
      highestQualification: userFormDataParse?.qualification,
      updateLead: true,
    };
    const url = `${RMS_PRE_ENROLLMENT_BASE_URL}pre-enrolment/student/generateRegId`;
    const token = process.env.PRE_ENROLLMENT_UNIVERSAL_ACCESS_TOKEN;

    try {
      const response = await axios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.data.statusCode !== 201) {
        throw new Error(`Unexpected API response: ${response.data.message}`);
      }

      const newRegId: any = response?.data?.data?.leadData?.studentCode;
      const applicationId: any = response?.data?.data?.applicationData?.id;
      const applicationCode: any =
        response?.data?.data?.applicationData?.applicationCode;

      if (!newRegId || !applicationId || !applicationCode) {
        throw new Error(
          "Missing registration or application ID in API response."
        );
      }

      return { newRegId, applicationId, applicationCode };
    } catch (error) {
      console.error("err", error);
    }
  };

  const handleVineCrmUpdatedProgram = async () => {
    const crmCountryCode = userFormDataParse?.phone?.startsWith("+234")
      ? "NIGERIA"
      : userFormDataParse.phone.startsWith("+254")
      ? "KENYA"
      : userFormDataParse.phone.startsWith("+27")
      ? "SOUTH AFRICA"
      : userFormDataParse.phone.startsWith("+256")
      ? "UGANDA"
      : userFormDataParse.phone.startsWith("+255")
      ? "TANZANIA"
      : null;

    let payload = null;
    if (crmCountryCode) {
      payload = {
        domain: "crm",
        type: "add_lead_to_crm",
        name: userFormDataParse.name,
        email: userFormDataParse.email,
        mobile: userFormDataParse.phone,
        interest: userSelectedProgram,
        country: crmCountryCode ? crmCountryCode : geoLocationData?.countryName,
        city: geoLocationData?.city || "",
        source: "SA Website",
        utm_source: "SA Website",
        utm_url: window.location.href,
        utm_referrer: window.location.href,
      };
    }

    try {
      const crmAPi = VIN_CRM_API_BASE_URL;

      if (!crmAPi) {
        throw new Error("CRM API URL is not defined.");
      }
      await axios.post(crmAPi, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (error) {
      console.error("Error in handleVineCrmUpdatedProgram:", error);
    }
  };

  const handleReserveClick = async () => {
    if (!selectedProgram) return;

    setIsLoading(true);
    const crmCountryCode = userFormDataParse?.phone?.startsWith("+234")
      ? "NIGERIA"
      : userFormDataParse.phone.startsWith("+254")
      ? "KENYA"
      : userFormDataParse.phone.startsWith("+27")
      ? "SOUTH AFRICA"
      : userFormDataParse.phone.startsWith("+256")
      ? "UGANDA"
      : userFormDataParse.phone.startsWith("+255")
      ? "TANZANIA"
      : null;

    if (
      crmCountryCode &&
      userSelectedProgram !== userFormDataParse?.Programme_Of_Interest
    ) {
      handleVineCrmUpdatedProgram();
    }

    try {
      const regData = await handleCreateRegId();
      if (!regData) {
        console.error("Error creating registration ID");
        setIsLoading(false);
        return;
      }

      const { newRegId, applicationId, applicationCode } = regData;
      localStorage.setItem("regData", JSON.stringify(regData));
      localStorage.setItem("totalFee", JSON.stringify(applicationTotalFee));

      const totalFees = applicationTotalFee;
      const email = userFormDataParse?.email;

      const paymentData = await createPaymentData(
        totalFees,
        newRegId,
        applicationId,
        email,
        userSelectedProgram,
        applicationCode
      );

      if (!paymentData) {
        console.error("Error generating HMAC token");
        setIsLoading(false);
        return;
      }

      const form = document.createElement("form");
      form.action = PAYMENT_GATEWAY;
      form.method = "POST";

      Object.keys(paymentData).forEach((key) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = String(paymentData[key as keyof typeof paymentData]);
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error("Error in handleReserveClick:", error);
      setIsLoading(false);

      setTimeout(() => {
        router.push(
          "https://rbs-pre-enroll.regenesys.digital//preEnrollmentPaymentFail"
        );
      }, 5000);
    }
  };

  if (!selectedProgram) {
    return <p>No program selected.</p>;
  }

  return (
    <div className={styles.PricingSection}>
      <div
        className={styles.Pricingcontainer}
        style={{ height: isMobile ? "auto" : `${windowHeight - 60}px` }}
      >
        <div onClick={onBack} className={styles.cancleDesktop}>
          <ArrowLeft />
        </div>
        <div className={styles.TopTag}>
          <div className={styles.headTopTag}>
            <div onClick={onBack}>
              <ChevronLeft />
            </div>
            <span>ⓘ 2 minutes to get provisional enrolment</span>
          </div>
          <div className={styles.gifContainer}>
            <Image
              src="/assets/tick-gif.gif"
              alt="Tick GIF"
              title="Tick GIF"
              width={88}
              height={88}
              className={styles.gif}
              priority
            />
          </div>
          <h2>
            Congratulations! You meet the initial eligibility for{" "}
            {selectedProgram?.name}
          </h2>
          <p>
            Final eligibility is subject to document verification. An academic
            counselor will assist you with the next steps.
          </p>
        </div>

        <div className={styles.card}>
          {rmatFee === 0 ? (
            <p>
              Secure your seat by Paying the Application Fee ( non-refundable )
            </p>
          ) : (
            <p>
              Secure your spot by paying the application fee of R{" "}
              {applicationFee}
              and the RMAT fee of R {rmatFee} (non-refundable)
            </p>
          )}
          <h3>R {applicationTotalFee}</h3>
          <div className={styles.programDetails}>
            <div className={styles.Tag}>
              <Check />
              Selected Programme
            </div>
            <div className={styles.programDetailsImage}>
              <Image
                src="/assets/small_log.png"
                alt="timer"
                title="timer"
                className={styles.image}
                width={26}
                height={32}
              />
              <p>{selectedProgram?.name}</p>
            </div>
            <div className={styles.programDetailsContent}>
              <div>
                <span>{selectedProgram?.noOfYear} Years |</span>
                <span>Programme Fee: R {formatFee(totalFee)}</span>
              </div>
              {isMobile ? (
                <button
                  className={styles.paymentPlanButton}
                  onClick={togglePlans}
                >
                  Available Payment Plans
                </button>
              ) : (
                <button
                  className={styles.paymentPlanButton}
                  onClick={togglePlans}
                  onMouseEnter={() => setShowPlans(true)}
                  onMouseLeave={() => setShowPlans(false)}
                >
                  Available Payment Plans
                </button>
              )}
            </div>
          </div>
          <p className={styles.planAid}>
            <Info style={{ fontSize: "18px" }} /> Financial Aid options are
            available
          </p>
          {showPlans && (
            <div
              className={styles.pricingCardSection}
              onMouseEnter={() => setShowPlans(true)}
              onMouseLeave={() => setShowPlans(false)}
            >
              <div
                className={`${styles.pricingCard} ${
                  isMobile ? styles.mobilePopup : ""
                }`}
              >
                {isMobile && (
                  <button className={styles.closeButton} onClick={closePlans}>
                    ✕
                  </button>
                )}
                <h3>Available Payment Plans </h3>
                {pricing.map((plan, index) => {
                  const planKey = Object.keys(plan)[0] as keyof PricingPlan;
                  const details = plan[planKey];
                  return (
                    details && (
                      <div key={index} className={styles.plan}>
                        <h4>{details?.text}</h4>
                        <p>{details?.price}</p>
                        <span>{details?.duration}</span>
                      </div>
                    )
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {isLoading && (
          <div className={styles.loaderPopup}>
            <div className={styles.loaderPopupContainer}>
              <div className={styles.loader}></div>
              <p>
                Redirecting to the payment gateway. Please do not close the next
                window or press back while we confirm your payment status.
              </p>
            </div>
          </div>
        )}
      </div>
      <div className={styles.buttonsContainer}>
        <button
          className={styles.submitButton}
          onClick={() => {
            handleReserveClick();
            trackEventPreEnrolment("reserve_seat_final_clicked", {
              application_fee: applicationFee,
            });
          }}
        >
          Reserve Your Seat
        </button>
      </div>
    </div>
  );
};

export default EligibilityPricingContainer;
